import { useEffect, useRef, useState } from "react";
import appIcon from "./assets/app-icon.svg";
import journey from "./assets/screens/journey.png";
import practice from "./assets/screens/practice.png";
import wordOrder from "./assets/screens/word-order.png";
import note from "./assets/screens/note.png";
import library from "./assets/screens/library.png";
import { content, type Locale } from "./content";
import { LessonVideos } from "./LessonVideos";

const apkUrl = import.meta.env.VITE_APK_URL || "/downloads/lingogram-0.3.1.apk";
const appVersion = import.meta.env.VITE_APP_VERSION || "0.3.1";
const screenImages = [journey, practice, note, library];
const navTargets = ["method", "experience", "questions"];
// Official merchant-supplied markup: preserve the seal URL, code, and referrer policy.
const enamadMarkup =
  "<a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=7753052&Code=DQZsPDP6pACSSVhXcSrYEgozruz3y4MF'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=7753052&Code=DQZsPDP6pACSSVhXcSrYEgozruz3y4MF' alt='' style='cursor:pointer' code='DQZsPDP6pACSSVhXcSrYEgozruz3y4MF'></a>";

function initialLocale(): Locale {
  const query = new URLSearchParams(window.location.search).get("lang");
  if (query === "en" || query === "fa") return query;
  try {
    if (localStorage.getItem("lingogram-locale") === "en") return "en";
  } catch {
    /* Storage is optional. */
  }
  return "fa";
}

function Icon({
  name,
  className = "",
}: {
  name:
    | "arrow"
    | "download"
    | "play"
    | "check"
    | "globe"
    | "book"
    | "path"
    | "spark";
  className?: string;
}) {
  const paths = {
    arrow: "M5 12h14m-6-6 6 6-6 6",
    download: "M12 3v12m-4-4 4 4 4-4M5 17v4h14v-4",
    play: "m9 5 11 7-11 7V5Z",
    check: "m5 12 4 4L19 6",
    globe:
      "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM3 12h18M12 3c-5 5-5 13 0 18 5-5 5-13 0-18Z",
    book: "M12 5C9 3 5 3 3 4v15c3-1 6-1 9 1 3-2 6-2 9-1V4c-2-1-6-1-9 1Zm0 0v15",
    path: "M6 4h10a4 4 0 0 1 0 8H8a4 4 0 0 0 0 8h10M5 2v4m12 12 3 2-3 2",
    spark: "m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z",
  };
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
}

function Phone({
  src,
  alt,
  priority = false,
  className = "",
}: {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
}) {
  return (
    <div className={`phone ${className}`}>
      <div className="phone-screen">
        <img
          src={src}
          alt={alt}
          width="1080"
          height="2316"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
        />
      </div>
    </div>
  );
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [menuOpen, setMenuOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [screen, setScreen] = useState(0);
  const menuButton = useRef<HTMLButtonElement>(null);
  const t = content[locale];
  const isFa = locale === "fa";
  const number = (n: number) =>
    new Intl.NumberFormat(isFa ? "fa-IR" : "en").format(n);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isFa ? "rtl" : "ltr";
    document.title = t.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t.description);
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", t.title);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", t.description);
    try {
      localStorage.setItem("lingogram-locale", locale);
    } catch {
      /* Private browsing still works. */
    }
    const url = new URL(window.location.href);
    url.searchParams.set("lang", locale);
    window.history.replaceState(null, "", url);
  }, [locale, isFa, t]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [menuOpen]);

  useEffect(() => {
    const sync = () => {
      setLocale(initialLocale());
      setMenuOpen(false);
    };
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const download = (className = "") => (
    <a className={`button button-primary ${className}`} href={apkUrl} download>
      <Icon name="download" />
      <span>{t.download}</span>
    </a>
  );
  const brand = (
    <a className="brand" href="#top" aria-label={t.brand}>
      <img src={appIcon} width="30" height="30" alt="" />
      <span>{t.brand}</span>
    </a>
  );

  return (
    <div id="top">
      <a className="skip-link" href="#main">
        {t.skip}
      </a>
      <header className="site-header">
        <div className="container header-inner">
          {brand}
          <nav
            id="main-nav"
            className={`nav ${menuOpen ? "is-open" : ""}`}
            aria-label={t.menu}
          >
            {t.nav.map((label, i) => (
              <a
                key={navTargets[i]}
                href={`#${navTargets[i]}`}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="header-actions">
            <button
              className="language-switch"
              type="button"
              onClick={() => {
                setLocale(isFa ? "en" : "fa");
                setMenuOpen(false);
              }}
              aria-label={isFa ? "Switch to English" : "تغییر زبان به فارسی"}
              lang={isFa ? "en" : "fa"}
            >
              <Icon name="globe" />
              <span>{isFa ? "EN" : "فارسی"}</span>
            </button>
            <a className="header-download" href="#download">
              {t.download}
              <Icon name="arrow" className="directional" />
            </a>
            <button
              ref={menuButton}
              className={`menu-toggle ${menuOpen ? "is-open" : ""}`}
              type="button"
              aria-label={menuOpen ? t.close : t.menu}
              aria-expanded={menuOpen}
              aria-controls="main-nav"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <main id="main">
        <section className="hero container" aria-labelledby="hero-title">
          <a className="announcement" href="#lessons">
            <span className="live-dot" />
            {t.badge}
            <Icon name="arrow" className="directional" />
          </a>
          <h1 id="hero-title">
            {t.hero[0]}
            <br />
            <span>{t.hero[1]}</span>
          </h1>
          <p className="hero-description">{t.intro}</p>
          <div className="hero-actions">
            {download()}
            <a className="button button-ghost" href="#lessons">
              {t.explore}
              <Icon name="arrow" className="directional" />
            </a>
          </div>
          <div className="hero-meta">
            <span>{t.android}</span>
            <i />
            <span>{t.direct}</span>
            <i />
            <span>
              {t.version} <bdi>{appVersion}</bdi>
            </span>
          </div>
          <div className="hero-stage" dir="ltr">
            <div className="stage-grid" />
            <div className="orbit orbit-one" />
            <div className="orbit orbit-two" />
            <div className="stage-label stage-label-left">
              <span className="tiny-cross">+</span>
              <span>
                REAL CONTEXT.
                <br />
                LASTING CONNECTIONS.
              </span>
            </div>
            <Phone
              className="hero-phone phone-left"
              src={note}
              alt={t.galleryTabs[2]}
              priority
            />
            <Phone
              className="hero-phone phone-center"
              src={journey}
              alt={t.galleryTabs[0]}
              priority
            />
            <Phone
              className="hero-phone phone-right"
              src={practice}
              alt={t.galleryTabs[1]}
              priority
            />
            <div className="stage-label stage-label-right">
              <span className="live-dot" />
              <span>
                DESIGNED FOR
                <br />
                YOUR EVERYDAY.
              </span>
            </div>
            <span className="stage-coordinate">LINGOGRAM / ANDROID</span>
          </div>
          <p className="capture-note">
            <span className="live-dot" />
            {t.screenNote}
          </p>
        </section>

        <LessonVideos locale={locale} />

        <div className="pillars container">
          {t.pillars.map((label, i) => (
            <span key={label}>
              <Icon name={(["play", "book", "check", "path"] as const)[i]} />
              {label}
            </span>
          ))}
        </div>

        <section className="section container philosophy">
          <div>
            <span className="eyebrow">
              <span />
              {t.philosophyLabel}
            </span>
            <h2>
              {t.philosophy}
              <br />
              <span className="muted-heading">{t.philosophyAccent}</span>
            </h2>
          </div>
          <div className="philosophy-detail">
            <p>{t.philosophyBody}</p>
            <div className="phrase-example">
              <span className="micro-label">{t.exampleLabel}</span>
              <div className="phrase" lang="en" dir="ltr">
                <span>make</span>
                <span className="phrase-plus">+</span>
                <span>a decision</span>
                <Icon name="check" />
              </div>
              <span className="phrase-translation">{t.exampleTranslation}</span>
            </div>
          </div>
        </section>

        <section
          className="section container method"
          id="method"
          aria-labelledby="method-title"
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                <span />
                {t.methodLabel}
              </span>
              <h2 id="method-title">{t.methodTitle}</h2>
            </div>
            <p>{t.methodBody}</p>
          </div>
          <div className="method-layout">
            <div className="method-steps">
              {t.steps.map((item, i) => (
                <button
                  className={`method-step ${step === i ? "active" : ""}`}
                  key={i}
                  type="button"
                  aria-pressed={step === i}
                  aria-controls="method-preview"
                  onClick={() => setStep(i)}
                >
                  <span className="step-index">
                    {number(i + 1).padStart(2, isFa ? "۰" : "0")}
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <span className="step-body">{item.body}</span>
                  </span>
                  <Icon name="arrow" className="directional" />
                </button>
              ))}
            </div>
            <div className="method-preview" id="method-preview">
              <div className="preview-topline">
                <span className="live-dot" />
                <span>{t.steps[step].label}</span>
                <span className="preview-count" dir="ltr">
                  0{step + 1} / 03
                </span>
              </div>
              <Phone
                src={[practice, note, wordOrder][step]}
                alt={t.steps[step].title}
              />
              <div className="preview-bottomline">
                <Icon name="play" />
                {t.actualScreen}
              </div>
            </div>
          </div>
        </section>

        <section
          className="section container experience"
          aria-labelledby="experience-title"
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                <span />
                {t.experienceLabel}
              </span>
              <h2 id="experience-title">{t.experienceTitle}</h2>
            </div>
            <p>{t.experienceBody}</p>
          </div>
          <div className="feature-grid">
            <article className="feature-card journey-card">
              <div className="card-copy">
                <Icon name="path" />
                <h3>{t.journeyTitle}</h3>
                <p>{t.journeyBody}</p>
              </div>
              <div className="journey-window">
                <img
                  src={journey}
                  width="1080"
                  height="2316"
                  loading="lazy"
                  alt={t.galleryTabs[0]}
                />
              </div>
            </article>
            <article className="feature-card library-card">
              <div className="card-copy">
                <Icon name="book" />
                <h3>{t.libraryTitle}</h3>
                <p>{t.libraryBody}</p>
              </div>
              <div className="library-window">
                <img
                  src={library}
                  width="1080"
                  height="2316"
                  loading="lazy"
                  alt={t.galleryTabs[3]}
                />
              </div>
            </article>
            <article className="feature-card progress-card">
              <div className="progress-visual" aria-hidden="true">
                <div className="progress-ring">
                  <Icon name="check" />
                </div>
                <div className="progress-track">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </div>
              <div className="card-copy">
                <Icon name="spark" />
                <h3>{t.progressTitle}</h3>
                <p>{t.progressBody}</p>
              </div>
            </article>
          </div>
        </section>

        <section
          className="section container gallery"
          id="experience"
          aria-labelledby="gallery-title"
        >
          <div className="gallery-copy">
            <span className="eyebrow">
              <span />
              {t.galleryLabel}
            </span>
            <h2 id="gallery-title">{t.galleryTitle}</h2>
            <p>{t.galleryBody}</p>
            <div className="screen-selector" aria-label={t.galleryTitle}>
              {t.galleryTabs.map((label, i) => (
                <button
                  key={i}
                  type="button"
                  className={screen === i ? "selected" : ""}
                  aria-pressed={screen === i}
                  aria-controls="gallery-preview"
                  onClick={() => setScreen(i)}
                >
                  <span className="screen-number">0{i + 1}</span>
                  {label}
                  <Icon name="arrow" className="directional" />
                </button>
              ))}
            </div>
            <p className="screen-description" aria-live="polite">
              {t.galleryDescriptions[screen]}
            </p>
            <span className="language-note">{t.screenshotLanguage}</span>
          </div>
          <div className="gallery-preview" id="gallery-preview">
            <span className="gallery-annotation" dir="ltr">
              FIG. 0{screen + 1} — LINGOGRAM
            </span>
            <Phone src={screenImages[screen]} alt={t.galleryTabs[screen]} />
            <span className="gallery-caption">
              <span className="live-dot" />
              {t.actualScreen}
            </span>
          </div>
        </section>

        <section
          className="section container faq"
          id="questions"
          aria-labelledby="faq-title"
        >
          <div>
            <span className="eyebrow">
              <span />
              {t.faqLabel}
            </span>
            <h2 id="faq-title">{t.faqTitle}</h2>
          </div>
          <div className="faq-list">
            {t.faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>
                  {question}
                  <span className="faq-plus" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section
          className="download-section"
          id="download"
          aria-labelledby="download-title"
        >
          <div className="container download-inner">
            <img
              className="download-logo"
              src={appIcon}
              width="64"
              height="64"
              alt=""
            />
            <span className="eyebrow">{t.ctaLabel}</span>
            <h2 id="download-title">{t.ctaTitle}</h2>
            <p>{t.ctaBody}</p>
            {download()}
            <div className="hero-meta">
              <span>{t.requirement}</span>
              <i />
              <span>
                {t.version} <bdi>{appVersion}</bdi>
              </span>
            </div>
            <ol className="install-steps">
              {t.install.map((label, i) => (
                <li key={i}>
                  <span>{number(i + 1)}</span>
                  {label}
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>

      <footer className="container">
        <div className="footer-main">
          <div>
            {brand}
            <p>{t.footer}</p>
          </div>
          <nav aria-label={isFa ? "پیوندهای پایین صفحه" : "Footer navigation"}>
            {t.nav.map((label, i) => (
              <a href={`#${navTargets[i]}`} key={label}>
                {label}
              </a>
            ))}
            <a href="#download">{t.download}</a>
          </nav>
          <div className="trust-section">
            <div
              className="trust-badge"
              dangerouslySetInnerHTML={{ __html: enamadMarkup }}
            />
            <a
              className="trust-caption"
              referrerPolicy="origin"
              target="_blank"
              href="https://trustseal.enamad.ir/?id=7753052&Code=DQZsPDP6pACSSVhXcSrYEgozruz3y4MF"
            >
              {isFa ? "نماد اعتماد الکترونیکی" : "eNAMAD trust seal"}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © {number(new Date().getFullYear())} {t.brand}. {t.rights}
          </span>
          <a href="#top">
            {t.back}
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
